import { cva, type VariantProps } from 'class-variance-authority';
import React from 'react';

import { cn } from '@commenda/utils';

/** The icon button used across chat surfaces — the panel header and message action rows. Kakashi's
 * `IconButton` squares off at rounded-md and hovers to the tertiary-button fill; chat's is a 6px
 * square that fills bg-tertiary, so it lives here. */
const chatIconButtonVariants = cva(
  'inline-flex shrink-0 cursor-pointer items-center justify-center rounded-sm bg-transparent text-fg-secondary outline-none transition-colors hover:bg-bg-tertiary focus-visible:ring-2 focus-visible:ring-fg-tertiary disabled:pointer-events-none disabled:text-fg-quaternary',
  {
    variants: {
      size: {
        sm: 'size-6 p-xs',
        md: 'size-7 p-sm',
      },
      isActive: {
        true: 'bg-bg-tertiary',
      },
    },
    defaultVariants: {
      size: 'sm',
    },
  }
);

interface ChatIconButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof chatIconButtonVariants> {
  /** 16px, whatever the button's own size. */
  icon: React.ReactNode;
  'aria-label': string;
}

export const ChatIconButton = React.forwardRef<
  HTMLButtonElement,
  ChatIconButtonProps
>(({ className, size, isActive, icon, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn(chatIconButtonVariants({ size, isActive }), className)}
    {...props}
  >
    {icon}
  </button>
));

ChatIconButton.displayName = 'ChatIconButton';
